/**
 * The `<selected_documents>` block the backend prepends to a user message when
 * the composer had files/contracts attached. It is request plumbing, not
 * something the user typed — so every surface that renders a *restored*
 * transcript has to strip it back out and re-derive the references from it.
 */

export interface ParsedSelectedDocuments {
  /** The user's own text, with the block removed. */
  content: string;
  selectedDocumentsIncluded?: boolean;
  selectedDocumentCount?: number;
  /** Display names carried in the block itself (review-project grammar). */
  selectedDocumentNames?: string[];
  /** Context keys for uid-only lines (general grammar) — names resolve separately. */
  selectedDocumentKeys?: string[];
}

// The general chat's line grammar — a cross-repo contract with the backend's
// `_compose_message`: one `- [marker] {uid}` line per item, no names (the backend
// deliberately doesn't resolve them). The review project's block differs: its marker
// IS the revision uid and its content is a bare file name.
const GENERAL_SELECTION_MARKER_PREFIXES: Record<string, string> = {
  file: 'file-',
  contract: 'contract-',
  'contract file': 'cfile-',
};

interface SelectedDocumentRefs {
  names: string[];
  keys: string[];
}

export function extractSelectedDocumentRefs(selectedDocumentsBlock: string): SelectedDocumentRefs {
  const names: string[] = [];
  const keys: string[] = [];
  let nextContentLineIsFileName = false;

  for (const rawLine of selectedDocumentsBlock.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;

    const markerMatch = line.match(/^-\s+\[([^\]]+)\]\s*(.*)$/);
    if (markerMatch) {
      const marker = markerMatch.at(1)?.trim() ?? '';
      const content = markerMatch.at(2)?.trim() ?? '';
      const keyPrefix = GENERAL_SELECTION_MARKER_PREFIXES[marker];
      if (keyPrefix) {
        // The content is a raw uid — never a display name. It must not reach the UI;
        // the name is resolved from the uid like the composer chips do.
        if (content) keys.push(`${keyPrefix}${content}`);
        nextContentLineIsFileName = false;
      } else if (content) {
        names.push(content);
        nextContentLineIsFileName = false;
      } else {
        nextContentLineIsFileName = true;
      }
      continue;
    }

    if (nextContentLineIsFileName) {
      names.push(line);
      nextContentLineIsFileName = false;
    }
  }

  return { names, keys };
}

export function parseSelectedDocumentsMessage(content: string): ParsedSelectedDocuments {
  const selectedDocumentsMatch = content.match(
    /^\s*<selected_documents>\s*([\s\S]*?)\s*<\/selected_documents>\s*/
  );

  if (!selectedDocumentsMatch) {
    return { content };
  }

  const selectedDocumentsBlock = selectedDocumentsMatch.at(1) ?? '';
  const { names, keys } = extractSelectedDocumentRefs(selectedDocumentsBlock);
  const selectedDocumentCount = selectedDocumentsBlock
    .split('\n')
    .filter(line => /^-\s+\[[^\]]+\]/.test(line.trim())).length;

  return {
    content: content.slice(selectedDocumentsMatch[0].length).trim(),
    selectedDocumentsIncluded: true,
    selectedDocumentCount: selectedDocumentCount > 0 ? selectedDocumentCount : undefined,
    selectedDocumentNames: names.length > 0 ? names : undefined,
    selectedDocumentKeys: keys.length > 0 ? keys : undefined,
  };
}
